import { useCallback, useEffect, useRef } from 'react'
import { useInView } from 'react-intersection-observer'
import type { Product } from '@velasca/types'
import { StampedReviewsDesktopSkeleton } from './components/StampedReviewsDesktopSkeleton'
import { StampedReviewsMobileSkeleton } from './components/StampedReviewsMobileSkeleton'

export const StampedReviews = ({
  productId,
  title,
  handle,
  description,
  url,
  featuredImage,
}: Product): JSX.Element | null => {
  const [inViewRef, inView] = useInView({
    rootMargin: `800px 0px 800px 0px`,
  })
  const containerElementRef = useRef<HTMLDivElement | null>(null)

  const setRefs = useCallback(
    (node: HTMLDivElement | null) => {
      containerElementRef.current = node
      inViewRef(node)
    },
    [inViewRef],
  )

  useEffect(() => {
    if (!inView || !containerElementRef.current) {
      return
    }

    window.StampedFn?.init()

    const observer = new MutationObserver(() => {
      const stampedContainer =
        containerElementRef.current?.querySelector('.stamped-container')

      if (stampedContainer) {
        stampedContainer.setAttribute('data-widget-show-qna', 'false')
        stampedContainer.setAttribute(
          'data-widget-show-filter-custom-forms',
          'false',
        )
        stampedContainer.setAttribute('data-widget-show-filter-search', 'false')
        stampedContainer.setAttribute('data-widget-show-tab-reviews', 'false')
        stampedContainer.setAttribute('data-widget-show-sort', 'false')
        observer.disconnect()
      }
    })

    observer.observe(containerElementRef.current, {
      childList: true,
      subtree: true,
    })

    return () => {
      observer.disconnect()
    }
  }, [inView])

  return (
    <div
      ref={setRefs}
      id="stamped-main-widget"
      className="stamped-apparel-widget"
      data-widget-style="traditional"
      data-product-id={productId}
      data-name={title}
      data-url={url}
      data-image-url={featuredImage}
      data-description={description}
      data-product-sku={handle}
      data-limit-words="80"
    >
      <StampedReviewsMobileSkeleton />
      <StampedReviewsDesktopSkeleton />
    </div>
  )
}
